/**
 * UTM 参数和 URL Snapshot 工具函数
 */

const FIRST_PAGE_URL_KEY = 'first_page_url';
const UTM_PARAMS_KEY = 'utm_params';

// UTM 参数字段列表
const UTM_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_campaign_id',
  'utm_adgroup',
  'utm_adgroup_id',
  'utm_ad',
  'utm_matchtype',
  'utm_device',
  'utm_term',
] as const;

export interface UtmParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_campaign_id: string | null;
  utm_adgroup: string | null;
  utm_adgroup_id: string | null;
  utm_ad: string | null;
  utm_matchtype: string | null;
  utm_device: string | null;
  utm_term: string | null;
}

/**
 * 从当前 URL 获取 UTM 参数
 * 注意：空值返回 null（符合架构要求）
 */
const getUtmParamsFromUrl = (): UtmParams => {
  const urlParams = new URLSearchParams(window.location.search);

  return {
    utm_source: urlParams.get('utm_source') || null,
    utm_medium: urlParams.get('utm_medium') || null,
    utm_campaign: urlParams.get('utm_campaign') || null,
    utm_campaign_id: urlParams.get('utm_campaign_id') || null,
    utm_adgroup: urlParams.get('utm_adgroup') || null,
    utm_adgroup_id: urlParams.get('utm_adgroup_id') || null,
    utm_ad: urlParams.get('utm_ad') || null,
    utm_matchtype: urlParams.get('utm_matchtype') || null,
    utm_device: urlParams.get('utm_device') || null,
    utm_term: urlParams.get('utm_term') || null,
  };
};

/**
 * 初始化 UTM 参数（网站启动时调用）
 * 只在首次访问时记录，后续导航不会覆盖
 */
export const initUtmParams = (): void => {
  if (!sessionStorage.getItem(UTM_PARAMS_KEY)) {
    const utmParams = getUtmParamsFromUrl();
    sessionStorage.setItem(UTM_PARAMS_KEY, JSON.stringify(utmParams));
    console.log('🏷️ UTM params recorded:', utmParams);
  }
};

/**
 * 获取 UTM 参数（优先从 sessionStorage 读取）
 */
export const getUtmParams = (): UtmParams => {
  const stored = sessionStorage.getItem(UTM_PARAMS_KEY);
  if (stored) {
    try {
      return JSON.parse(stored) as UtmParams;
    } catch {
      // 解析失败，返回空值
    }
  }
  // 如果 sessionStorage 中没有，从 URL 获取
  return getUtmParamsFromUrl();
};

/**
 * 初始化 first_page_url（网站启动时调用）
 * 只在首次访问时记录，后续导航不会覆盖
 */
export const initFirstPageUrl = (): void => {
  if (!sessionStorage.getItem(FIRST_PAGE_URL_KEY)) {
    const firstPageUrl = window.location.href;
    sessionStorage.setItem(FIRST_PAGE_URL_KEY, firstPageUrl);
    console.log('🔗 First page URL recorded:', firstPageUrl);
  }
};

/**
 * 获取 first_page_url（用户首次进入网站的完整 URL）
 */
export const getFirstPageUrl = (): string => {
  return sessionStorage.getItem(FIRST_PAGE_URL_KEY) || window.location.href;
};

/**
 * 获取 landing_page（当前页面路径）
 */
export const getLandingPage = (): string => {
  return window.location.pathname;
};

/**
 * 获取 user_agent
 */
export const getUserAgent = (): string => {
  return navigator.userAgent;
};

/**
 * 打印 UTM 参数（用于调试）
 */
export const logUtmParams = (): void => {
  const params = getUtmParams();
  console.log('========== UTM Parameters ==========');
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      console.log(`${key}: ${value}`);
    }
  });
  console.log('first_page_url:', getFirstPageUrl());
  console.log('landing_page:', getLandingPage());
  console.log('====================================');
};

